// Generisches Such-Sheet (Bottom-Sheet) — EINE einheitliche Popup-Optik für
// Chain-, Wallet- und sonstige Listen-Auswahl. iOS-Pattern:
//   • öffnet OHNE Tastatur (kein autoFocus) — die ganze Liste ist sofort sichtbar
//   • Tap auf die Suchleiste → „Search-Mode": Grabber/Titel klappen weg, die Such-
//     leiste pinnt nach oben (+ Abbrechen), die Treffer füllen den Platz über der
//     Tastatur (kleiner Screen → maximaler Ergebnis-Raum)
//   • Filterung ist ENTPRELLT (useDeferredValue) + gekappt → kein Ruckeln
//   • Sektionen via `sections` (z.B. „Meine Wallets" vs. „Vorgeschlagen")
//
// items:    [{ key, label, sub?, logo, current?, accent?, onPick }]   (flach)
// sections: [{ title, items: [...] }]                                  (gruppiert)
// onCustom(text): optionaler Freitext-Eintrag (z.B. „Andere Wallet").

import { useState, useEffect, useMemo, useDeferredValue, useRef } from 'react';
import { createPortal } from 'react-dom';
import { F, withAlpha } from '../styles/theme';
import { useTheme } from '../context/ThemeContext';
import { useT } from '../i18n/useT';
import { Icon, Icons } from './red/Icons';

const CAP = 60; // max gerenderte Treffer (Performance auf langen Listen)

function Row({ onClick, T, RED, logo, label, sub, current, accent }) {
  const baseBg = accent ? withAlpha(accent, 0.10) : (current ? withAlpha(T.text, 0.05) : 'transparent');
  return (
    <button type="button" onClick={onClick} style={{
      width: '100%', display: 'flex', alignItems: 'center', gap: 13, padding: '11px 12px', minHeight: 48,
      background: baseBg, border: 'none', borderRadius: 12,
      color: T.text, textAlign: 'left', cursor: 'pointer', fontFamily: F.family, fontSize: 15, fontWeight: 600,
      transition: 'background .12s', WebkitTapHighlightColor: 'transparent',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = accent ? withAlpha(accent, 0.16) : withAlpha(T.text, 0.06); }}
      onMouseLeave={e => { e.currentTarget.style.background = baseBg; }}
    >
      {logo}
      <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <span style={{ letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.text }}>{label}</span>
        {sub && <span style={{ fontSize: 12, fontWeight: 500, color: T.sub, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>}
      </span>
      {current && <Icon d={Icons.check} size={18} stroke={RED} sw={2.4} />}
    </button>
  );
}

export default function PickerSheet({ open, onClose, title, searchPlaceholder, items = [], sections = null, onCustom, customPrefix, emptyText = '—', closeOnPick = true }) {
  const { T } = useTheme();
  const { t } = useT();
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const deferredQ = useDeferredValue(q); // entprellt: Tippen bleibt flüssig, Filtern verzögert

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  useEffect(() => { if (open) { setQ(''); setSearching(false); } }, [open]);

  const norm = deferredQ.trim().toLowerCase();
  const matchFn = (it) => !norm
    || String(it.label || '').toLowerCase().includes(norm)
    || String(it.sub || '').toLowerCase().includes(norm)
    || String(it.keywords || '').toLowerCase().includes(norm);

  // Sektionen ODER flache items — beides unterstützt, Treffer auf CAP gedeckelt.
  const groups = useMemo(() => {
    const src = (sections && sections.length) ? sections : [{ title: null, items }];
    let remaining = CAP;
    const out = [];
    for (const g of src) {
      if (remaining <= 0) break;
      const matched = (g.items || []).filter(matchFn);
      if (!matched.length) continue;
      const capped = matched.slice(0, remaining);
      remaining -= capped.length;
      out.push({ title: g.title, items: capped });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, items, norm]);

  if (!open) return null;
  const RED = T.red || '#E4544A';
  const allItems = (sections && sections.length) ? sections.flatMap(g => g.items || []) : items;
  const showCustom = !!onCustom && q.trim().length > 0 && !allItems.some(it => String(it.label || '').toLowerCase() === norm);
  const totalShown = groups.reduce((n, g) => n + g.items.length, 0);

  const cancelSearch = () => { setSearching(false); setQ(''); try { inputRef.current?.blur(); } catch (_) {} };

  const sheet = (
    <div onClick={onClose} role="dialog" aria-modal="true" style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(8px) saturate(130%)', WebkitBackdropFilter: 'blur(8px) saturate(130%)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      animation: 'psFadeIn 200ms ease',
    }}>
      <style>{`@keyframes psFadeIn{from{opacity:0}to{opacity:1}}@keyframes psUp{from{transform:translateY(100%)}to{transform:translateY(0)}}@media (prefers-reduced-motion: reduce){[data-pssheet]{animation:none!important}}`}</style>
      <div data-pssheet data-kb-overlay onClick={e => e.stopPropagation()} style={{
        width: '100%', maxWidth: 480,
        maxHeight: searching ? '94vh' : '80vh',
        display: 'flex', flexDirection: 'column',
        background: T.surface,
        borderTop: `1px solid ${T.line}`, borderLeft: `1px solid ${T.line}`, borderRight: `1px solid ${T.line}`,
        borderRadius: '22px 22px 0 0',
        boxShadow: '0 -16px 50px rgba(0,0,0,0.40)',
        overflow: 'hidden',
        animation: 'psUp 300ms cubic-bezier(0.22,1,0.36,1)',
        transition: 'max-height 240ms cubic-bezier(0.16,1,0.3,1)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}>
        {/* Grabber + Titel — im Search-Mode ausgeblendet für maximalen Listen-Platz */}
        {!searching && (
          <>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
              <div style={{ width: 38, height: 5, borderRadius: 999, background: withAlpha(T.text, 0.18) }} />
            </div>
            {title && <div style={{ fontFamily: F.display, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', color: T.text, padding: '8px 16px 10px' }}>{title}</div>}
          </>
        )}

        {/* Suchleiste — gepinnt. Im Search-Mode mit „Abbrechen", sonst mit X. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: searching ? '12px 16px 8px' : '0 16px 10px' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: withAlpha(T.text, 0.05), borderRadius: 12, padding: '0 12px', height: 44 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onFocus={() => setSearching(true)}
              placeholder={searchPlaceholder} enterKeyHint="search" autoComplete="off" autoCorrect="off" spellCheck={false}
              style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none', color: T.text, fontFamily: F.family, fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' }} />
            {q && (
              <button type="button" onClick={() => { setQ(''); try { inputRef.current?.focus(); } catch (_) {} }} aria-label={t('clear') || 'Leeren'}
                style={{ width: 20, height: 20, borderRadius: '50%', border: 'none', background: withAlpha(T.text, 0.12), color: T.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, fontSize: 12, lineHeight: 1 }}>×</button>
            )}
          </div>
          {searching ? (
            <button type="button" onClick={cancelSearch} style={{ background: 'none', border: 'none', color: RED, fontFamily: F.family, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: '0 2px' }}>{t('cancel')}</button>
          ) : (
            <button type="button" onClick={onClose} aria-label={t('cancel')}
              style={{ width: 34, height: 34, borderRadius: '50%', border: `1px solid ${T.line}`, background: 'transparent', color: T.sub, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          )}
        </div>

        {/* Ergebnis-Liste */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 10px 0', paddingBottom: 'calc(14px + var(--kbpad))', transition: 'padding-bottom var(--dur-kb) var(--ease-kb)', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          {showCustom && (
            <Row T={T} RED={RED} onClick={() => { onCustom(q.trim()); if (closeOnPick) onClose(); }}
              label={`${customPrefix ? customPrefix + ' ' : ''}„${q.trim()}"`}
              logo={<span style={{ width: 32, height: 32, borderRadius: '50%', background: withAlpha(T.text, 0.08), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: T.sub, fontWeight: 800, flexShrink: 0 }}>+</span>} />
          )}
          {totalShown === 0 && !showCustom ? (
            <div style={{ padding: '28px 10px', textAlign: 'center', color: T.mute, fontSize: 13, fontFamily: F.family }}>{emptyText}</div>
          ) : groups.map((g, gi) => (
            <div key={gi}>
              {g.title && (
                <div style={{ fontSize: 12, fontWeight: 600, color: T.mute || T.sub, padding: '12px 12px 6px', letterSpacing: '-0.005em' }}>{g.title}</div>
              )}
              {g.items.map(it => (
                <Row key={it.key} T={T} RED={RED} logo={it.logo} label={it.label} sub={it.sub} current={it.current} accent={it.accent}
                  onClick={() => { it.onPick?.(); if (closeOnPick) onClose(); }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  return createPortal(sheet, document.body);
}
